import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useId, useEffect } from 'react';
import { useCombinedRefs, twMerge } from '@belong/common';
import clsx from 'clsx';
import css from './TextArea.module.css.js';

const TextArea = /*#__PURE__*/forwardRef(function TextArea({
  id,
  value = '',
  name,
  label,
  error,
  focused,
  RightElement,
  variant = 'primary',
  className,
  ...rest
}, ref) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const innerRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, innerRef);
  const isFocused = focused || isInputFocused;
  const _id = id || useId();
  const labelId = `label-${_id}`;
  const _value = value;
  function onFocus(event) {
    if (rest.onFocus) {
      rest.onFocus(event);
    }
    setIsInputFocused(true);
  }
  function onBlur(event) {
    if (rest.onBlur) {
      rest.onBlur(event);
    }
    setIsInputFocused(false);
  }
  function handleChange(event) {
    if (rest.onChange) {
      rest.onChange(event);
    }
  }
  function resizeTextArea() {
    if (combinedRef.current) {
      combinedRef.current.style.height = 'auto';
      combinedRef.current.style.height = combinedRef.current.scrollHeight + 'px';
    }
  }
  useEffect(resizeTextArea, [_value]);
  return jsxs("div", {
    className: twMerge(clsx('relative flex w-full items-end border-0 border-b-2 border-solid border-gray focus-within:border-green', css.container, {
      'border-green': focused || variant === 'secondary' && value
    }, {
      'border-red': !!error
    })),
    children: [jsx("textarea", {
      className: twMerge('w-full pb-sm outline-none mt-lg resize-none leading-body', variantToInputClassNameMap[variant], rest.disabled && 'text-gray', className),
      id: _id || name,
      name: name,
      value: _value,
      ref: combinedRef,
      ...rest,
      onFocus: onFocus,
      onBlur: onBlur,
      onChange: handleChange,
      rows: 1
    }), RightElement && jsx("div", {
      className: clsx('absolute right-0', css.rightElement),
      children: RightElement
    }), jsx("label", {
      id: labelId,
      className: twMerge(clsx('pointer-events-none absolute left-0', variantToLabelClassNameMap[variant], {
        [clsx(css.lifted, variantToLiftedLabelClassNameMap[variant])]: value !== ''
      }, {
        'text-red': !!error
      }, {
        [clsx(css.focused, 'text-green')]: isFocused
      }, rest.disabled && 'text-gray', css.inputLabel)),
      htmlFor: _id || name,
      children: label
    })]
  });
});
const variantToInputClassNameMap = {
  primary: '',
  secondary: 'text-white'
};
const variantToLabelClassNameMap = {
  primary: 'text-dark-gray',
  secondary: 'text-white'
};
const variantToLiftedLabelClassNameMap = {
  primary: 'text-navy',
  secondary: 'text-green'
};

export { TextArea };
